import { api } from '@/services/ApiService'
import projectStore from '@/store/ProjectStore'
import formStore from '@/store/FormStore'
import { ProjectResponse } from '@/services/models/ProjectResponse'
import {
  BusEvent,
  CharacteristicType,
  ComparisonFuelType,
  CoolingCalculationMethod,
  CustomArticleApiType,
  CustomArticleType,
  CustomPriceType,
  EnergySource,
  GlazedSurface,
  HeatingCalculationMethod,
  HpStep,
  InstallationType,
  IsolationType,
  KeyNumType,
  ProductIdType,
  ProductStatus,
  ProductType,
  ProjectPropertyKeyType,
  ReportVariant,
  StringMap
} from '@/helper/Types'
import { generateRandomId } from '@/helper/tools'
import { AccessoryCharacteristic } from '@/services/models/AccessoryCharacteristic'
import ProjectDataModel from '@/services/models/ProjectDataModel'
import FormController from '@/controllers/FormController'
import { CalculatedCoolingLoadRequest } from '@/services/models/CalculatedCoolingLoad'
import { CalculatedHeatingLoadRequest } from '@/services/models/CalculatedHeatingLoad'
import appStore from '@/store/AppStore'
import { ProductResponse } from '@/services/models/ProductResponse'
import userStore from '@/store/UserStore'
import i18n from '@/helper/Translations'
import { HPCharacteristic } from '@/services/models/HPCharacteristic'
import bus from '@/helper/Bus'
import { tagProjectStore } from '@/helper/analytics'
import { BufferTankSuggestion } from '@/services/models/BufferTankSuggestion'
import { Accessory } from '@/services/models/implementations/Accessory.ts'
import { Product } from '@/services/models/implementations/Product.ts'
import { Article } from '@/services/models/Article.ts'
import { Role } from '@/services/models/ArticleRole.ts'
import { Format } from '@/helper/Format.ts'

export default class ProjectController {
  private static readonly RESET_WHITE_LIST: ProjectPropertyKeyType[] = [
    'customArticles',
    'customPrices',
    'electricityPrice',
    'comparisonFuelTypes'
  ]
  private static _productDebounce: { [key: string]: boolean } = {
    [ProductType.BACKUPHEATER]: false,
    [ProductType.BUFFERTANK]: false,
    [ProductType.DHWTANK]: false,
    [ProductType.HEATPUMP]: false,
    [ProductType.ACCESSORY]: false,
    [ProductType.SYSTEM_SCHEME]: false,
    [ProductType.SYSTEM_CONTROLLER]: false,
  }

  public static async saveProject(projectId: string = projectStore.projectId): Promise<void> {
    projectStore.pushState() // save current project state to recover it if saving/mapping fails
    FormController.saveFormData()

    const projectData = ProjectController.saveMapping()

    try {
      if (projectId) {
        const res = await api.updateProject(projectId, projectData)
        ProjectController.loadMapping(res)
      } else {
        const res = await api.createProject(projectData)
        projectStore.projectId = res.id
        ProjectController.loadMapping(res)
      }
      projectStore.dropState()
    } catch (e) {
      projectStore.popState()
      throw Error((e as Error).message)
    } finally {
      projectStore.resetProducts()
    }
  }

  /**
   * Loading the project data into the projectProperties based on the previously set projectId (from router query)
   */
  public static async loadProject(projectId: string = projectStore.projectId): Promise<void> {
    //Loading project data when opening project

    if (projectId) {
      projectStore.projectId = projectId
    }

    appStore.isLoading = true
    try {
      const res = await api.loadProject(projectId)

      // Products needed for validation in validateStep()
      await Promise.all([
        ProjectController.loadProducts(ProductType.HEATPUMP, projectId),
        ProjectController.loadProducts(ProductType.SYSTEM_CONTROLLER, projectId),
        ProjectController.loadProducts(ProductType.BUFFERTANK, projectId),
        ProjectController.loadProducts(ProductType.BACKUPHEATER, projectId),
        ProjectController.loadProducts(ProductType.DHWTANK, projectId),
        ProjectController.loadProducts(ProductType.SALES_PACKAGE)
      ])

      ProjectController.loadMapping(res)
      bus.$emit(BusEvent.LOAD_PROJECT_FINISHED)
    } catch (e) {
      throw Error((e as Error).message)
    } finally {
      appStore.isLoading = false
    }
  }

  /**
   * Loads the products of the specified type via the API and saves/caches them in the _products object
   * @param productType
   * @param projectId
   * @param  additionalParam
   */
  public static async loadProducts(
    productType: ProductType,
    projectId: string = projectStore.projectId,
    additionalParam?: string
  ): Promise<void> {
    if (projectStore.products[productType] === undefined) {
      return
    }
    if (ProjectController._productDebounce[productType]) {
      return
    }

    appStore.isLoading = true
    ProjectController._productDebounce[productType] = true

    const productResponse = await api.loadProducts(projectId, productType, additionalParam)

    if (productType === ProductType.BUFFERTANK) {
      projectStore.bufferTankSuggestion = new BufferTankSuggestion(productResponse as unknown as BufferTankSuggestion)
      projectStore.products[productType] = projectStore.bufferTankSuggestion.all.length ? projectStore.bufferTankSuggestion.all : undefined
    } else {
      projectStore.products[productType] = productResponse.length ? productResponse : undefined
    }

    appStore.isLoading = false
    ProjectController._productDebounce[productType] = false
  }

  public static async loadAccessories(): Promise<void> {
    if (projectStore.accessories === undefined) {
      return
    }
    if (ProjectController._productDebounce[ProductType.ACCESSORY]) {
      return
    }
    appStore.isLoading = true
    ProjectController._productDebounce[ProductType.ACCESSORY] = true
    try {
      const productResponse = await api.loadProducts(projectStore.projectId, ProductType.ACCESSORY)
      //Undefined - To prevent loading loop if empty product response -> accessories loaded but empty
      const accessories = productResponse.map(a => Object.assign(new Accessory(), a))
      projectStore.accessories = accessories.length ? accessories : undefined
    } catch (e) {
      projectStore.accessories = undefined
      throw Error((e as Error).message)
    } finally {
      appStore.isLoading = false
      ProjectController._productDebounce[ProductType.ACCESSORY] = false
    }

    //Do not trigger the pre-population if current step is not accessories, e.g. summary step on project load
    if (appStore.currentStep !== HpStep.ACCESSORIES) {
      return
    }

    // Logic for pre populating amounts from accessories preseletion
    projectStore.preselectedAccessories = {}
    if (
      projectStore.accessories &&
      !Object.keys((projectStore.projectProperties.selectedAccessories.val || {}) as KeyNumType).length
    ) {
      for (const prod of projectStore.accessories) {
        const quantity = projectStore.getProductCharacteristic<AccessoryCharacteristic>(
          prod,
          CharacteristicType.ACCESSORY
        )?.suggestedQuantity
        if (quantity) {
          projectStore.preselectedAccessories[prod.id] = quantity
        }
      }
    }
  }

  public static async loadSystemSchemes(): Promise<void> {
    if (projectStore.systemSchemes === undefined) {
      return
    }

    if (ProjectController._productDebounce[ProductType.SYSTEM_SCHEME]) {
      return
    }

    appStore.isLoading = true

    ProjectController._productDebounce[ProductType.SYSTEM_SCHEME] = true

    try {
      const schemeResponse = await api.loadSystemSchemes(projectStore.projectId)
      projectStore.systemSchemes = schemeResponse.length ? schemeResponse : undefined
    } catch (e) {
      projectStore.systemSchemes = undefined
      throw Error((e as Error).message)
    } finally {
      appStore.isLoading = false
      ProjectController._productDebounce[ProductType.SYSTEM_SCHEME] = false
    }
  }

  private static getInstallationTypes(res: ProjectResponse): InstallationType[] {
    const installationTypes = []

    if (res.heatLoss || res.heatingBuildingInfo || res.heatingConsumptionInfo) {
      installationTypes.push(InstallationType.HEATING)
    }
    if (res.coolingLoad || res.coolingBuildingInfo) {
      installationTypes.push(InstallationType.COOLING)
    }

    if (!installationTypes.length) {
      installationTypes.push(InstallationType.HEATING)
    }

    return installationTypes
  }

  private static loadMapping(res: ProjectResponse) {
    projectStore.origin = res.origin

    const data: StringMap = {
      name: res.projectName,
      firstName: res.customerData?.firstName,
      lastName: res.customerData?.lastName,
      customerStreet: res.customerData?.address?.street,
      customerHouseNumber: res.customerData?.address?.houseNumber,
      customerPostalCode: res.customerData?.address?.postalCode,
      customerCity: res.customerData?.address?.city,
      projectStreet: res.projectAddress?.street,
      projectHouseNumber: res.projectAddress?.houseNumber,
      projectPostalCode: res.projectAddress?.postalCode,
      projectCity: res.projectAddress?.city,
      projectAltitude: res.projectAddress?.altitude,
      phoneNumber: res.customerData?.phone,
      emailAddress: res.customerData?.email,
      heatingLoss: res.heatLoss,
      livingArea: Format.round(res.livingArea),
      coolingLoad: res.coolingLoad,
      constructionYear: res.constructionYear,
      buildingHull: res.buildingHull,
      powerOutputIntegratedBackupHeater: res.powerOutputIntegratedBackupHeater,
      glazedSurfaces: res.coolingBuildingInfo?.glazedSurfaces,
      coolingIndoorTemperature: res.coolingBuildingInfo?.indoorTemperature,
      coolingVentilationType: res.coolingBuildingInfo?.ventilationType,
      heatingIndoorTemperature: res.heatingBuildingInfo?.indoorTemperature,
      heatingVentilationType: res.heatingBuildingInfo?.ventilationType,
      windowType: res.heatingBuildingInfo?.windowType,
      previousFuelType: res.heatingConsumptionInfo?.previousFuelType,
      operatingHours: res.heatingConsumptionInfo?.operatingHours,
      annualConsumption: res.heatingConsumptionInfo?.annualConsumption,
      annualConsumptionUnit: res.heatingConsumptionInfo?.annualConsumptionUnit,
      adjoinedBuildingSides: res.heatingBuildingInfo?.adjoinedBuildingSides,
      isolation:
        res.heatingBuildingInfo === undefined || !Object.keys(res.heatingBuildingInfo).length
          ? undefined
          : [
            res.heatingBuildingInfo?.isolatedFloor ? IsolationType.FLOOR : '',
            res.heatingBuildingInfo?.isolatedRoof ? IsolationType.ROOF : '',
            res.heatingBuildingInfo?.isolatedWalls ? IsolationType.WALLS : ''
          ].filter(e => e !== ''),
      dhwEnabled: !!res.dhwDemand,
      dhwPersons: res.dhwDemand?.numberOfPersons,
      dhwDemand: res.dhwDemand?.demandPerPerson,
      circuits: res.circuits?.map(c => {
        // cooling only circuits are sent without a 'flowTemperature' field, this causes in an error by
        // deep equal comparison functions (step validity check) -> therefore flowTemperature gets set to undefined
        c.flowTemperature = Object.keys(c).includes('flowTemperature') ? c.flowTemperature : undefined
        return c
      }),
      heatSource: res.energySource,
      groundType: res.groundType,
      groundTypesEnabled: !!res.groundType,
      systemSchemeId: res.systemSchemeId,
      heatPumpPlacement: res.heatPumpPlacement,
      installationTypes: this.getInstallationTypes(res),
      explicitlyNoBufferTankSelected: res.explicitlyNoBufferTankSelected,
      explicitlyNoBackupHeaterSelected: res.explicitlyNoBackupHeaterSelected,
      explicitlyNoAccessoriesSelected: res.explicitlyNoAccessoriesSelected,
      missingAccessoriesConfirmed: res.missingAccessoriesConfirmed,
      electricityPrice: this.formatPrice(res.electricityPrice || userStore.electricityPrice),
      reportVariants: [ReportVariant.CONSUMER, ReportVariant.INSTALLER],
      heatingCalculationMethod: this.getHeatingCalculationMethod(res),
      coolingCalculationMethod: this.getCoolingCalculationMethod(res),
      comparisonFuelTypes: ProjectController.formatFuelTypePrices(res.comparisonFuelTypes || []),
      hybrid: res.hybrid,
      existingBackupHeater: res.existingBackupHeater,
      existingDhwTank: res.existingDhwTank,
      debugArchiveUrl: res.debugArchiveUrl,
      heatExchanger: res.heatExchanger,
      hybridKit: res.hybridKit,
      bufferStrategy: res.bufferStrategy,
      mountingMaterial: res.mountingMaterial,
      salePackageId: res.salePackageId,
      salePackage: res.salePackage,
    }
    projectStore.projectProperties.useExistingDhwTank.val = projectStore.projectProperties.existingDhwTank.val !== undefined
    // projectStore.projectProperties.useExistingDhwTank.val = !!data.existingDhwTank?.name

    //Assign data properties to project properties
    for (const property of Object.keys(data)) {
      if (!(property in projectStore.projectProperties)) {
        continue
      }
      projectStore.projectProperties[property as ProjectPropertyKeyType].val = data[property]
    }

    //Custom articles
    //Maps Custom Articles onto internal structure and adds the custom price key pairs if custom prices set
    projectStore.projectProperties.customPrices.val = {}

    const customArticles: CustomArticleType[] = []
    const responseCustomArticles = res.customArticles || []

    responseCustomArticles.forEach(article => {
      const generatedId = generateRandomId()
      customArticles.push({
        id: generatedId,
        name: article.name,
        price: article.price,
        quantity: article.quantity,
        group: article.group
      } as CustomArticleType)

      if (article.customTotalPrice !== undefined) {
        (projectStore.projectProperties.customPrices.val as KeyNumType)[generatedId] = article.customTotalPrice
      }
    })
    projectStore.projectProperties.customArticles.val = customArticles

    //Custom prices
    res.customPrices?.forEach(
      price => ((projectStore.projectProperties.customPrices.val as KeyNumType)[price.productId] = price.price)
    )
    projectStore.projectProperties.customPrices.val = Object.keys(projectStore.projectProperties.customPrices.val).length
      ? projectStore.projectProperties.customPrices.val
      : undefined

    const products = ProjectController.transformArticlesToProducts(res.articles || [])

    projectStore.selectedArticles = res.articles?.map(a => new Article(a)) || []
    projectStore.salesPackages = res.salesPackages?.map(a => new Article(a)) || []

    //Products
    projectStore.resetSelectedProducts()

    const additionalProducts = this.extractProductsByCharacteristics(products, [
      CharacteristicType.AIR_COLLECTOR,
      CharacteristicType.HYDRAULIC_MODULE,
      CharacteristicType.HYDRAULIC_TOWER
    ])
    if (additionalProducts.length) {
      if (
        projectStore.processProductStatus(additionalProducts[0], projectStore.projectProperties.additionalProductId)
      ) {
        projectStore.projectProperties.additionalProductId.val = additionalProducts[0].id //There can only be one additional product
        projectStore.selectedProducts[ProductType.ADDITIONAL_PRODUCT] = additionalProducts[0]
      }
    }

    const systemController = this.extractProductsByCharacteristics(products, [CharacteristicType.SYSTEM_CONTROLLER])
    if (systemController.length) {
      if (projectStore.processProductStatus(systemController[0], projectStore.projectProperties.systemControllerId)) {
        projectStore.projectProperties.systemControllerId.val = systemController[0].id
        projectStore.selectedProducts[ProductType.SYSTEM_CONTROLLER] = systemController[0]
      }
    }

    const internetGateway = this.extractProductsByCharacteristics(products, [CharacteristicType.INTERNET_GATEWAY])
    if (internetGateway.length) {
      if (projectStore.processProductStatus(internetGateway[0], projectStore.projectProperties.internetGatewayId)) {
        projectStore.projectProperties.internetGatewayId.val = internetGateway[0].id
        projectStore.selectedProducts[ProductType.INTERNET_GATEWAY] = internetGateway[0]
      }
    }

    const remoteControls = this.extractProductsByCharacteristics(products, [CharacteristicType.REMOTE_CONTROL])
    if (remoteControls.length) {
      const quantity = res.articles?.find(a => a.details?.characteristics.some(chr => chr.type === CharacteristicType.REMOTE_CONTROL))?.quantity
      const rc = new Article({ id: remoteControls[0].id, details: new Product(remoteControls[0]), quantity })

      if (projectStore.processProductStatusStep(rc.details!, projectStore.projectProperties.remoteControl.step)) {
        projectStore.projectProperties.remoteControl.val = rc
        projectStore.selectedProducts[ProductType.REMOTE_CONTROL] = rc.details
      }
    }

    projectStore.projectProperties.controllerExtensions.val = []

    const controllerExtensions = res.articles
      ?.filter(a => a.details?.characteristics.find(chr => chr.type === CharacteristicType.CONTROLLER_EXTENSION)) || []

    for (const controllerExtension of controllerExtensions) {
      if (projectStore.processProductStatusStep(controllerExtension.details!, projectStore.projectProperties.controllerExtensions.step)) {
        projectStore.projectProperties.controllerExtensions.val.push(new Article({
          ...controllerExtension,
          quantity: controllerExtension.quantity
        }))
      }
    }

    const heatPumps = this.extractProductsByCharacteristics(products, [CharacteristicType.HEAT_PUMP])
    if (heatPumps.length) {
      if (projectStore.processProductStatus(heatPumps[0], projectStore.projectProperties.heatPumpId)) {
        projectStore.projectProperties.heatPumpId.val = heatPumps[0].id //There can only be one heat pump
        projectStore.projectProperties.heatPumpCascadeSize.val = (
          heatPumps[0].characteristics.find(chr => chr.type === CharacteristicType.HEAT_PUMP) as HPCharacteristic
        ).cascadeSize
        projectStore.selectedProducts[ProductType.HEATPUMP] = heatPumps[0]
      }
    }

    const backupHeaters = this.extractProductsByCharacteristics(products, [CharacteristicType.BACKUP_HEATER])
    if (backupHeaters.length) {
      if (projectStore.processProductStatus(backupHeaters[0], projectStore.projectProperties.backupHeaterId)) {
        projectStore.projectProperties.backupHeaterId.val = backupHeaters[0].id //There can only be one backup heater
        projectStore.selectedProducts[ProductType.BACKUPHEATER] = backupHeaters[0]
      }
    }

    const dhwStorages = this.extractProductsByCharacteristics(products, [CharacteristicType.DHW_STORAGE])
    if (dhwStorages.length) {
      if (projectStore.processProductStatus(dhwStorages[0], projectStore.projectProperties.dhwStorageId)) {
        projectStore.projectProperties.dhwStorageId.val = dhwStorages[0].id //There can only be one dhw storage
        projectStore.selectedProducts[ProductType.DHWTANK] = dhwStorages[0]
      }
    }

    const bufferTankChr = [CharacteristicType.BUFFER_TANK, CharacteristicType.COOLING_BUFFER_TANK]
    const bufferTanks = projectStore.selectedArticles
      .filter(a => a.details?.characteristics
        .some(c => bufferTankChr.includes(c.type as CharacteristicType)))

    if (bufferTanks.length) {
      const legacyProject = !bufferTanks.some(b => b.roles && b.roles.length)
      if (bufferTanks.length > 1) {

        let coolingBufferTank = bufferTanks.find(a => (a.roles || [])?.includes(Role.COOLING))
        let heatingBufferTank = bufferTanks.find(a => (a.roles || [])?.includes(Role.HEATING))

        if (legacyProject) {
          coolingBufferTank = bufferTanks.find(a => a.details?.getProductCharacteristic(CharacteristicType.COOLING_BUFFER_TANK))
          heatingBufferTank = bufferTanks.find(a => a.details?.getProductCharacteristic(CharacteristicType.BUFFER_TANK))
          if (!heatingBufferTank) {
            heatingBufferTank = bufferTanks.find(a => a.details?.getProductCharacteristic(CharacteristicType.COOLING_BUFFER_TANK))
          }
        }

        const heatingBufferTankProduct = heatingBufferTank?.details as ProductResponse
        const coolingBufferTankProduct = coolingBufferTank?.details as ProductResponse

        if (projectStore.processProductStatus(heatingBufferTankProduct, projectStore.projectProperties.bufferTankId)) {
          projectStore.projectProperties.bufferTankId.val = heatingBufferTankProduct.id
          projectStore.selectedProducts[ProductType.BUFFERTANK] = heatingBufferTankProduct
        }
        if (projectStore.processProductStatus(coolingBufferTankProduct, projectStore.projectProperties.coolingBufferTankId)) {
          projectStore.projectProperties.coolingBufferTankId.val = coolingBufferTankProduct.id
          projectStore.selectedProducts[ProductType.COOLING_BUFFERTANK] = coolingBufferTankProduct
        }
      } else {
        if (projectStore.processProductStatus(bufferTanks[0].details as ProductResponse, projectStore.projectProperties.bufferTankId)) {
          if (legacyProject && bufferTanks[0].quantity > 1) {
            const heatingBuffer = { ...bufferTanks[0], quantity: 1 }
            const coolingBuffer = { ...bufferTanks[0], quantity: 1 }
            if (projectStore.isHeating) {
              this.setHeatingBuffer(heatingBuffer.details)
            }
            if (projectStore.isCooling) {
              this.setCoolingBuffer(coolingBuffer.details)
            }
          } else {
            if (projectStore.isHeating) {
              this.setHeatingBuffer(bufferTanks[0].details)
            } else {
              this.setCoolingBuffer(bufferTanks[0].details)
            }
          }
        }
      }
    }

    const resolvedAccessories = ProjectController.transformAccessories(res.articles || [])

    //Accessories
    projectStore.resolvedAccessories = (resolvedAccessories || []).filter(acc =>
      projectStore.processProductStatus(acc, projectStore.projectProperties.selectedAccessories)
    )

    projectStore.projectProperties.selectedAccessories.val = {}

    // use the cleared (phased out products removed) list instead of the res.accessories list
    projectStore.resolvedAccessories.forEach(acc => {
      const quantity = projectStore.getProductCharacteristic<AccessoryCharacteristic>(
        acc,
        CharacteristicType.ACCESSORY
      )?.quantity
      if (quantity) {
        (projectStore.projectProperties.selectedAccessories.val as KeyNumType)[acc.id] = quantity
      }
    })

    //Appliance interfaces
    const applianceInterfaces = this.extractProductsByCharacteristics(products, [
      CharacteristicType.APPLIANCE_INTERFACE
    ])
    if (applianceInterfaces.length) {
      if (
        projectStore.processProductStatus(applianceInterfaces[0], projectStore.projectProperties.applianceInterfaceId)
      ) {
        projectStore.projectProperties.applianceInterfaceId.val = applianceInterfaces[0].id
        // push to resolvedAccessories to appear in summary accessory view
        projectStore.resolvedAccessories?.push(applianceInterfaces[0])
      }
    }

    //add stepSaleSet if selectedSaleSet
    if (res.salePackageId || res.salePackage) {
      appStore.setStepCondition('includeSaleSet', true)
    } else {
      appStore.setStepCondition('includeSaleSet', false)
    }

    //Data from Project Response only
    projectStore.totalPrice = res.totalPrice
    projectStore.status = res.status

    tagProjectStore(res)
  }

  private static setHeatingBuffer(heatingBuffer?: ProductResponse) {
    if (!heatingBuffer) {
      return
    }
    if (projectStore.processProductStatus(heatingBuffer, projectStore.projectProperties.bufferTankId)) {
      projectStore.projectProperties.bufferTankId.val = heatingBuffer.id
      projectStore.selectedProducts[ProductType.BUFFERTANK] = heatingBuffer
    }
  }

  private static setCoolingBuffer(coolingBuffer?: ProductResponse) {
    if (!coolingBuffer) {
      return
    }
    if (projectStore.processProductStatus(coolingBuffer, projectStore.projectProperties.coolingBufferTankId)) {
      projectStore.projectProperties.coolingBufferTankId.val = coolingBuffer.id
      projectStore.selectedProducts[ProductType.COOLING_BUFFERTANK] = coolingBuffer
    }
  }

  private static getHeatingCalculationMethod(res: ProjectResponse): HeatingCalculationMethod | undefined {
    if (res.heatingConsumptionInfo && userStore.enableHeatingConsumption) {
      return HeatingCalculationMethod.CONSUMPTION
    } else if (res.heatingBuildingInfo && userStore.enableHeatingThermalEnvelope) {
      return HeatingCalculationMethod.THERMAL_ENVELOPE
    }

    return HeatingCalculationMethod.MANUAL
  }

  private static getCoolingCalculationMethod(res: ProjectResponse): CoolingCalculationMethod | undefined {
    if (res.coolingBuildingInfo) {
      return CoolingCalculationMethod.THERMAL_ENVELOPE
    }

    return CoolingCalculationMethod.MANUAL
  }

  private static transformArticlesToProducts(articles: Article[]): ProductResponse[] {
    return articles?.map(article => {
      const product = { ...article.details }
      const heatPumpChr = product.characteristics?.find(
        chr => chr.type === CharacteristicType.HEAT_PUMP
      ) as HPCharacteristic

      if (heatPumpChr) {
        heatPumpChr.cascadeSize = article.quantity
      }

      return product as ProductResponse
    })
  }

  private static transformAccessories(articles: Article[]): ProductResponse[] {
    return articles?.map(article => {
      const accIndex = article.details?.characteristics?.findIndex(char => char.type === CharacteristicType.ACCESSORY)

      if (accIndex !== undefined && accIndex >= 0) {
        (article.details!.characteristics[accIndex] as AccessoryCharacteristic).quantity = article.quantity
      }

      return article
    })?.map(article => ({ ...article.details } as ProductResponse))
      .filter(item => item.characteristics?.some(char => char.type === CharacteristicType.ACCESSORY))
      .filter(item => !item.characteristics?.some(char => char.type === CharacteristicType.SYSTEM_CONTROLLER))
      .filter(item => !item.characteristics?.some(char => char.type === CharacteristicType.INTERNET_GATEWAY))
      .filter(item => !item.characteristics?.some(char => char.type === CharacteristicType.REMOTE_CONTROL))
      .filter(item => !item.characteristics?.some(char => char.type === CharacteristicType.CONTROLLER_EXTENSION))
  }

  private static formatPrice(price: number): number {
    const result = userStore.enableIrregularCurrency ? price : price * 100
    // fixes floating point precision issue
    return +result.toFixed(2)
  }

  private static reformatPrice(price: number): number {
    const priceAsFloat = parseFloat(price?.toString().replace(',', '.'))
    return userStore.enableIrregularCurrency ? priceAsFloat : priceAsFloat / 100
  }

  private static formatFuelTypePrices(comparisonFuelTypes: ComparisonFuelType[]): ComparisonFuelType[] {
    if (comparisonFuelTypes.length === 0) {
      return []
    }

    return comparisonFuelTypes.map(c => ({
      fuelType: c.fuelType,
      price: ProjectController.formatPrice(c.price)
    }))
  }

  private static reformatFuelTypePrices(comparisonFuelTypes: ComparisonFuelType[]): ComparisonFuelType[] {
    if (comparisonFuelTypes.length === 0) {
      return []
    }

    return comparisonFuelTypes.map(c => ({
      fuelType: c.fuelType,
      price: ProjectController.reformatPrice(c.price)
    }))
  }

  /**
   * Returns matching products for the given characteristic from the products response
   * Deletes the matching products from the passed products object to prevent products with multiple characteristics
   * to be extracted multiple times
   * @param products
   * @param characteristics
   * @private
   */
  private static extractProductsByCharacteristics(
    products: ProductResponse[],
    characteristics: CharacteristicType[]
  ): ProductResponse[] {
    const foundProducts: ProductResponse[] = []
    let found: boolean

    do {
      found = false
      const i = products.findIndex(p =>
        p.characteristics?.some(c => characteristics.includes(c.type as CharacteristicType))
      )
      if (i >= 0) {
        foundProducts.push(products[i])
        products.splice(i, 1)

        found = true
      }
    } while (found)

    return foundProducts
  }

  private static saveMapping(): ProjectDataModel {
    const projectData: ProjectDataModel = {
      projectName: projectStore.projectProperties.name.val + '',
      heatLoss: projectStore.projectProperties.heatingLoss.val,
      livingArea: projectStore.projectProperties.livingArea.val,
      coolingLoad: projectStore.projectProperties.coolingLoad.val,
      constructionYear: projectStore.projectProperties.constructionYear.val,
      coolingBuildingInfo: projectStore.isCooling
        ? {
          indoorTemperature: projectStore.projectProperties.coolingIndoorTemperature.val as number,
          glazedSurfaces: projectStore.projectProperties.glazedSurfaces.val as GlazedSurface[],
          ventilationType: projectStore.projectProperties.coolingVentilationType.val as string
        }
        : undefined,
      heatingConsumptionInfo: projectStore.isHeatingCalculatedByConsumption
        ? {
          previousFuelType: projectStore.projectProperties.previousFuelType.val,
          operatingHours: projectStore.projectProperties.operatingHours.val,
          annualConsumption: projectStore.projectProperties.annualConsumption.val,
          annualConsumptionUnit: projectStore.projectProperties.annualConsumptionUnit.val
        }
        : undefined,
      heatingBuildingInfo: this.getHeatingBuildingInfo(),
      buildingHull: projectStore.projectProperties.buildingHull.val,
      articles: [],
      customArticles: undefined,
      customPrices: undefined,
      customerData: {
        firstName: projectStore.projectProperties.firstName.val,
        lastName: projectStore.projectProperties.lastName.val,
        phone: projectStore.projectProperties.phoneNumber.val,
        email: projectStore.projectProperties.emailAddress.val,
        address: {
          street: projectStore.projectProperties.customerStreet.val,
          postalCode: projectStore.projectProperties.customerPostalCode.val,
          houseNumber: projectStore.projectProperties.customerHouseNumber.val,
          city: projectStore.projectProperties.customerCity.val
        }
      },
      projectAddress: {
        street: projectStore.projectProperties.projectStreet.val,
        houseNumber: projectStore.projectProperties.projectHouseNumber.val,
        postalCode: projectStore.projectProperties.projectPostalCode.val as string,
        city: projectStore.projectProperties.projectCity.val as string,
        altitude: projectStore.projectProperties.projectAltitude.val
      },
      systemSchemeId: projectStore.projectProperties.systemSchemeId.val,
      dhwDemand: undefined,
      circuits: projectStore.projectProperties.circuits.val,
      energySource: projectStore.projectProperties.heatSource.val,
      groundType:
        projectStore.projectProperties.groundTypesEnabled.val &&
        projectStore.projectProperties.heatSource.val === EnergySource.GROUND_VERTICAL &&
        projectStore.projectProperties.groundType.val ? projectStore.projectProperties.groundType.val : undefined,
      heatPumpPlacement: projectStore.projectProperties.heatPumpPlacement.val,
      hybrid: projectStore.projectProperties.hybrid.val,
      existingBackupHeater: projectStore.projectProperties.existingBackupHeater.val,
      existingDhwTank: projectStore.projectProperties.existingDhwTank.val,
      electricityPrice: this.reformatPrice(projectStore.projectProperties.electricityPrice.val as number) || undefined,
      comparisonFuelTypes: this.reformatFuelTypePrices(projectStore.projectProperties.comparisonFuelTypes.val || []),
      reportVariants: projectStore.reportVariants,
      heatExchanger: projectStore.projectProperties.heatExchanger.val || false,
      hybridKit: projectStore.projectProperties.hybridKit.val || false,
      bufferStrategy: projectStore.projectProperties.bufferStrategy.val,
      mountingMaterial: projectStore.projectProperties.mountingMaterial.val,
      origin: projectStore.origin,
      salePackage: projectStore.projectProperties.salePackage.val || undefined,
      powerOutputIntegratedBackupHeater: projectStore.projectProperties.powerOutputIntegratedBackupHeater.val || undefined
    }

    tagProjectStore(projectData)

    if (projectData.electricityPrice === userStore.electricityPrice) {
      projectData.electricityPrice = undefined
    }

    if (
      !projectStore.projectProperties.customerStreet.val &&
      !projectStore.projectProperties.customerPostalCode.val &&
      !projectStore.projectProperties.customerHouseNumber.val &&
      !projectStore.projectProperties.customerCity.val
    ) {
      if (projectData.customerData) {
        projectData.customerData.address = undefined
      }
    }

    if (
      !projectStore.projectProperties.firstName.val &&
      !projectStore.projectProperties.lastName.val &&
      !projectStore.projectProperties.emailAddress.val &&
      !projectStore.projectProperties.phoneNumber.val &&
      !projectData.customerData?.address
    ) {
      projectData.customerData = undefined
    }

    if (projectStore.projectProperties.heatingCalculationMethod.val === HeatingCalculationMethod.THERMAL_ENVELOPE) {
      projectData.heatingConsumptionInfo = undefined
    } else if (projectStore.projectProperties.heatingCalculationMethod.val === HeatingCalculationMethod.CONSUMPTION) {
      projectData.heatingBuildingInfo = undefined
    } else {
      projectData.heatingBuildingInfo = undefined
      projectData.heatingConsumptionInfo = undefined
    }

    if (projectStore.projectProperties.coolingCalculationMethod.val === CoolingCalculationMethod.MANUAL) {
      projectData.coolingBuildingInfo = undefined
    }

    // Delete old entered data after switching from calculate to manual enter
    if (!projectData.heatingBuildingInfo && !projectData.coolingBuildingInfo) {
      projectData.buildingHull = undefined
      projectData.constructionYear = undefined
    }

    if (projectStore.projectProperties.dhwPersons.val && projectStore.projectProperties.dhwDemand.val) {
      if (projectStore.projectProperties.dhwEnabled.val) {
        projectData.dhwDemand = {
          numberOfPersons: projectStore.projectProperties.dhwPersons.val as number,
          demandPerPerson: projectStore.projectProperties.dhwDemand.val as number
        }
      }
    }

    if (projectStore.projectProperties.explicitlyNoBufferTankSelected.val !== undefined) {
      projectData.explicitlyNoBufferTankSelected = projectStore.projectProperties.explicitlyNoBufferTankSelected.val
    }

    if (projectStore.projectProperties.explicitlyNoBackupHeaterSelected.val !== undefined) {
      projectData.explicitlyNoBackupHeaterSelected = projectStore.projectProperties.explicitlyNoBackupHeaterSelected.val
    }

    if (projectStore.projectProperties.explicitlyNoAccessoriesSelected.val !== undefined) {
      projectData.explicitlyNoAccessoriesSelected = projectStore.projectProperties.explicitlyNoAccessoriesSelected.val
    }

    if (projectStore.projectProperties.salePackageId.val !== undefined) {
      projectData.salePackageId = projectStore.projectProperties.salePackageId.val
    }

    if (projectStore.projectProperties.missingAccessoriesConfirmed.val !== undefined) {
      projectData.missingAccessoriesConfirmed = projectStore.projectProperties.missingAccessoriesConfirmed.val
    }

    (
      [
        ProductIdType.BACKUPHEATER,
        ProductIdType.ADDITIONAL_PRODUCT
      ] as ProjectPropertyKeyType[]
    ).forEach(idType => {
      if (projectStore.projectProperties[idType].val) {
        projectData.articles?.push({
          id: projectStore.projectProperties[idType].val as string,
          quantity: 1
        })
      }
    })

    if (projectStore.projectProperties[ProductIdType.COOLING_BUFFERTANK].val) {
      projectData.articles?.push({
        id: projectStore.projectProperties[ProductIdType.COOLING_BUFFERTANK].val as string,
        quantity: 1,
        roles: [Role.COOLING]
      })
    }

    if (projectStore.projectProperties[ProductIdType.BUFFERTANK].val) {
      let roles = [Role.HEATING]

      if (projectStore.isCooling && projectStore.isHeating && !projectStore.projectProperties[ProductIdType.COOLING_BUFFERTANK].val) {
        roles = [Role.HEATING, Role.COOLING]
      }

      projectData.articles?.push({
        id: projectStore.projectProperties[ProductIdType.BUFFERTANK].val as string,
        quantity: 1,
        roles
      })
    }

    if (projectStore.projectProperties[ProductIdType.APPLIANCE_INTERFACE].val) {
      projectData.articles?.push({
        id: projectStore.projectProperties[ProductIdType.APPLIANCE_INTERFACE].val as string,
        quantity: projectStore.projectProperties.heatPumpCascadeSize.val as number
      })
    }

    if (!projectStore.projectProperties.useExistingDhwTank.val && projectStore.projectProperties.dhwStorageId.val) {
      projectData.articles?.push({
        id: projectStore.projectProperties.dhwStorageId.val as string,
        quantity: 1
      })
    }

    if (projectStore.projectProperties.systemControllerId.val) {
      projectData.articles?.push({
        id: projectStore.projectProperties.systemControllerId.val,
        quantity: 1
      })
    }

    if (projectStore.projectProperties.internetGatewayId.val) {
      projectData.articles?.push({
        id: projectStore.projectProperties.internetGatewayId.val,
        quantity: 1
      })
    }

    if (projectStore.projectProperties.remoteControl.val?.id && projectStore.projectProperties.remoteControl.val.quantity > 0) {
      projectData.articles?.push({
        id: projectStore.projectProperties.remoteControl.val.id,
        quantity: projectStore.projectProperties.remoteControl.val.quantity
      })
    }

    if (projectStore.projectProperties.controllerExtensions.val && projectStore.projectProperties.controllerExtensions.val.length > 0) {
      for (const controllerExtension of projectStore.projectProperties.controllerExtensions.val) {
        if (controllerExtension.quantity > 0) {
          projectData.articles?.push({
            id: controllerExtension.id,
            quantity: controllerExtension.quantity
          })
        }
      }
    }

    // Add selected heatpump to articles
    if (projectStore.projectProperties.heatPumpId.val) {
      projectData.articles?.push({
        id: projectStore.projectProperties.heatPumpId.val as string,
        quantity: projectStore.projectProperties.heatPumpCascadeSize.val as number
      })
    }

    const customPricesVal = (projectStore.projectProperties.customPrices.val || {}) as KeyNumType
    const customArticlesVal = (projectStore.projectProperties.customArticles.val || []) as CustomArticleType[]

    // Maps custom articles and inserts the custom prices
    projectData.customArticles = (projectData.customArticles || [])?.concat(
      customArticlesVal.map(customArticle => ({
          name: customArticle.name,
          price: customArticle.price,
          customTotalPrice: customPricesVal[customArticle.id],
          quantity: customArticle.quantity,
          group: customArticle.group
        } as CustomArticleApiType)
      )
    )

    // Assemble one list of articles out of articles and selected accessories
    const allArticles: Article[] = (projectData.articles || [])
      .concat(Object.keys(projectStore.projectProperties.selectedAccessories.val || {})
        .map(id => ({ id, quantity: (projectStore.projectProperties.selectedAccessories as any).val[id] }))
      )

    // Put all articles into project data before sending it
    projectData.articles = allArticles

    const salesPackages = projectStore.salesPackages ?? []

    // Maps custom prices on API custom prices structure
    // Removes entries if products not selected anymore and removes the custom articles entries
    if (Object.keys(customPricesVal).length) {
      projectData.customPrices = Object.keys(customPricesVal)
        .filter(id => allArticles.some(article => article.id === id) || salesPackages.some(article => article.id === id))
        .filter(id => !Object.keys(customArticlesVal).includes(id))
        .map(
          id =>
            ({
              productId: id,
              price: customPricesVal[id]
            } as CustomPriceType)
        )
    }

    return projectData
  }

  private static getHeatingBuildingInfo() {
    let heatingBuildingInfo = undefined

    if (
      projectStore.isHeating &&
      (projectStore.projectProperties.heatingIndoorTemperature.val ||
        projectStore.projectProperties.heatingVentilationType.val ||
        projectStore.projectProperties.windowType.val ||
        projectStore.projectProperties.adjoinedBuildingSides.val ||
        projectStore.projectProperties.isolation.val)
    ) {
      heatingBuildingInfo = {
        indoorTemperature: projectStore.projectProperties.heatingIndoorTemperature.val,
        ventilationType: projectStore.projectProperties.heatingVentilationType.val,
        windowType: projectStore.projectProperties.windowType.val,
        adjoinedBuildingSides: projectStore.projectProperties.adjoinedBuildingSides.val,
        isolatedFloor: projectStore.projectProperties.isolation.val
          ? (projectStore.projectProperties.isolation.val as IsolationType[]).includes(IsolationType.FLOOR)
          : undefined,
        isolatedRoof: projectStore.projectProperties.isolation.val
          ? (projectStore.projectProperties.isolation.val as IsolationType[]).includes(IsolationType.ROOF)
          : undefined,
        isolatedWalls: projectStore.projectProperties.isolation.val
          ? (projectStore.projectProperties.isolation.val as IsolationType[]).includes(IsolationType.WALLS)
          : undefined
      }
    }
    return heatingBuildingInfo
  }

  static async calculateCoolingLoad(): Promise<number | null> {
    const req: CalculatedCoolingLoadRequest = new CalculatedCoolingLoadRequest(projectStore.projectProperties, formStore.data)

    // FIXME: Can we have some usable error message here?
    if (!req.validate()) {
      return null
    }

    return await api.calculateCoolingLoad(req)
  }

  static async calculateHeatingLoad(): Promise<number | null> {
    const req: CalculatedHeatingLoadRequest = new CalculatedHeatingLoadRequest(projectStore.projectProperties, formStore.data)

    // FIXME: Can we have some usable error message here?
    if (!req.validate()) {
      return null
    }

    return await api.calculateHeatingLoad(req)
  }

  public static async getProjectUpdateProducts(id: string): Promise<{
    phasedOut: ProductResponse[]
    priceUpdate: ProductResponse[]
    customPrices: KeyNumType
  }> {
    const res = await api.loadProject(id)

    ProjectController.loadMapping(res)

    const products = [...ProjectController.transformArticlesToProducts(res.articles || [])] as ProductResponse[]
    const salesPackageProducts = [...ProjectController.transformArticlesToProducts(res.salesPackages || [])] as ProductResponse[]
    const phasedOut = [...products, ...salesPackageProducts]
      .filter(product => product?.status && product.status === ProductStatus.PHASED_OUT)

    const customPrices = (projectStore.projectProperties.customPrices.val || {}) as KeyNumType
    const priceUpdate = [...products, ...salesPackageProducts]
      .filter(product => product?.updatedPrice && product.updatedPrice !== product.price)

    return { phasedOut, priceUpdate, customPrices }
  }

  public static async deleteProject(projectId: string = projectStore.projectId): Promise<void> {
    appStore.isLoading = true
    try {
      await api.deleteProject(projectId)
    } catch (e) {
      window.console.error('Deletion of project failed with id', projectId)
    }
    appStore.isLoading = false
  }

  public static async copyProject(projectId: string, copiedProjectName: string): Promise<ProjectResponse | null> {
    appStore.isLoading = true
    let projectResponse = null
    try {
      this.loadMapping(await api.loadProject(projectId))
      projectStore.projectProperties.name.val = copiedProjectName
      projectResponse = await api.createProject(this.saveMapping())
    } catch (e) {
      window.console.error('Copying project failed with id', projectId)
    } finally {
      appStore.isLoading = false
    }
    return projectResponse
  }

  /**
   * Checks all properties of step if fulfilling required statement
   * For products required only valid if products are available
   * @param step
   */
  public static validateStep(step: HpStep): boolean {
    for (const key of Object.keys(projectStore.projectProperties)) {
      const property = projectStore.projectProperties[key as ProjectPropertyKeyType]
      const required = projectStore.propertyIsRequired(key as ProjectPropertyKeyType)
      if (property.step !== step) {
        continue
      }

      if (property.productDependency === undefined) {
        if (Array.isArray(property.val)) {
          if (required && (!property.val?.length)) {
            console.debug('Validation failed', key)
            return false
          }
        } else if (required && property.val === undefined) {
          console.debug(`Validation failed: property ${key} is required but undefined`)
          return false
        }
      } else {
        if (
          required && // The property is required
          (projectStore.products[property.productDependency] || []).length && // There are products available
          !(projectStore.products[property.productDependency] || []).some(
            product => product.id === property.val
          )
        ) {
          //Check if product id found in referenced products array
          console.debug('Validation failed', key)
          return false
        }
      }
    }
    return true
  }

  public static resetFromStep(step: HpStep): void {
    for (const propertyName of Object.keys(projectStore.projectProperties)) {
      const prop = projectStore.projectProperties[propertyName as ProjectPropertyKeyType]
      if (this.RESET_WHITE_LIST.includes(propertyName as ProjectPropertyKeyType)) {
        continue
      }
      if (!appStore.isStepGreaterEqual(prop.step, step)) {
        continue
      }

      prop.val = undefined
    }

    for (const cstep of appStore.steps) {
      if (!appStore.isStepGreaterEqual(cstep.step, step)) {
        continue
      }
      appStore.setStepValidity(cstep.step, false)
    }

    if (appStore.isStepGreaterEqual(HpStep.ACCESSORIES, step)) {
      projectStore.accessories = []
    }

    if (appStore.isStepGreaterEqual(HpStep.SYSTEM_SCHEMES, step)) {
      projectStore.systemSchemes = []
    }

    //Cache invalidation products
    if (appStore.isStepGreaterEqual(HpStep.COMPONENTS, step)) {
      projectStore.products[ProductType.BACKUPHEATER] = []
      projectStore.products[ProductType.BUFFERTANK] = []
      projectStore.products[ProductType.DHWTANK] = []
      projectStore.products[ProductType.COOLING_BUFFERTANK] = []
    }

    //Cache invalidation heat pump
    if (appStore.isStepGreaterEqual(HpStep.HEAT_PUMP_SYSTEM, step)) {
      projectStore.products[ProductType.HEATPUMP] = []
    }

    //Cache invalidation system controllers
    if (appStore.isStepGreaterEqual(HpStep.SYSTEM_CONTROLLERS, step)) {
      projectStore.products[ProductType.SYSTEM_CONTROLLER] = []
    }

    if (appStore.isStepGreaterEqual(HpStep.SALE_SET, step)) {
      projectStore.products[ProductType.SALES_PACKAGE] = []
    }
  }

  public static getProductTitle(item: ProductResponse): string {
    if ('products' in (item || {})) {
      return i18n.global.t('global.sales_package') //Sales package
    }

    const CHAR_TITLE_MAP: { [key: string]: string } = {
      [CharacteristicType.HEAT_PUMP]:
        'components.type_heat_pump_' + projectStore.projectProperties.heatSource.val?.toString().split('_')[0],
      [CharacteristicType.BACKUP_HEATER]: 'step_summary.title_backup_heater',
      [CharacteristicType.BUFFER_TANK]: 'step_summary.title_buffer',
      [CharacteristicType.COOLING_BUFFER_TANK]: 'step_summary.title_buffer',
      [CharacteristicType.DHW_STORAGE]: 'step_summary.title_dhw'
    }

    return projectStore
      .getAllCharacteristicTypes(item as ProductResponse)
      .filter(chr => chr in CHAR_TITLE_MAP)
      .map(chr => i18n.global.t(CHAR_TITLE_MAP[chr]))
      .join(', ')
  }
}
